import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_JOB_EDIT_HISTORY } from '../../../graphql/queries';

const FIELD_LABELS = {
  assignedToId: 'Assigned person',
  assignedTeamId: 'Assigned team',
  billedAmount: 'Billed amount',
  customFlags: 'Flags',
  hasDateOverride: 'Date override',
  hasTimeOverride: 'Time override',
  notes: 'Notes',
  payout: 'Payout',
  scheduledDate: 'Scheduled date',
  serviceTypeId: 'Service type',
  startTime: 'Start time',
  status: 'Status',
};

const displayValue = value => {
  if (value === null || value === undefined || value === '') return 'None';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    if (value.value) return value.value;
    if (Array.isArray(value)) return value.map(item => item.value || item.id).join(', ') || 'None';
    return JSON.stringify(value);
  }
  return String(value);
};

const parseChanges = changes => {
  try {
    return typeof changes === 'string' ? JSON.parse(changes) : changes || {};
  } catch {
    return {};
  }
};

const editorName = entry => {
  const editor = entry.editedBy;
  if (!editor) return entry.source === 'SYSTEM' ? 'System' : 'Unknown user';
  return editor.name || [editor.firstName, editor.lastName].filter(Boolean).join(' ') || editor.email;
};

const JobEditHistory = ({ jobId, refreshKey }) => {
  const { data, loading, error, refetch } = useQuery(GET_JOB_EDIT_HISTORY, {
    variables: { jobId },
    skip: !jobId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (refreshKey > 0) {
      refetch();
    }
  }, [refreshKey, refetch]);

  const history = data?.jobEditHistory || [];

  return (
    <details className="mt-4 border border-gray-200 rounded-lg">
      <summary className="px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer font-medium text-gray-900">
        Edit History {history.length > 0 ? `(${history.length})` : ''}
      </summary>
      <div className="p-4 space-y-3">
        {loading && <p className="text-sm text-gray-500">Loading edit history...</p>}
        {error && <p className="text-sm text-red-600">Edit history could not be loaded.</p>}
        {!loading && !error && history.length === 0 && (
          <p className="text-sm text-gray-500">No tracked edits yet.</p>
        )}
        {history.map(entry => {
          const changes = parseChanges(entry.changes);
          return (
            <div key={entry.id} className="border-l-2 border-sky-200 pl-3">
              <div className="text-sm font-medium text-gray-800">
                {editorName(entry)}
                <span className="font-normal text-gray-500">
                  {' · '}
                  {new Date(entry.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="mt-1 space-y-1">
                {Object.entries(changes).map(([field, change]) => (
                  <div key={field} className="text-xs text-gray-600 break-words">
                    <span className="font-medium">{FIELD_LABELS[field] || field}:</span>{' '}
                    {displayValue(change.before)} → {displayValue(change.after)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </details>
  );
};

export default JobEditHistory;
